/**
 * Recarga automática quando um pedaço do app não carrega.
 *
 * O PORQUÊ: o front é servido por `serve` com fallback de SPA — qualquer caminho
 * inexistente devolve o `index.html`. Durante a troca de versão, uma aba aberta pede um
 * pedaço com hash da build ANTERIOR, o arquivo já não existe, e o navegador recebe HTML
 * onde esperava JavaScript. O React quebra na tela de erro.
 *
 * Isso não é hipótese: aconteceu em produção em 28/08/2026 na tela de Pacientes.
 *
 * O `serve.json` desta mesma correção impede que essa resposta seja GUARDADA (`no-cache`),
 * então recarregar resolve — o `index.html` novo traz os nomes novos. Este módulo faz a
 * recarga sozinho, para que a pessoa não precise saber disso.
 *
 * A TRAVA: se o pedaço estiver genuinamente quebrado, recarregar em laço deixaria a tela
 * piscando para sempre. Só recarregamos se a última recarga foi há mais de JANELA_MS.
 * Falhando de novo dentro da janela, deixamos o ErrorBoundary aparecer — erro visível é
 * melhor que laço invisível.
 */

export const CHAVE_RECARGA = 'chunk-reload-em';
export const JANELA_MS = 10_000;

export function deveRecarregar(agora: number, marcaAnterior: string | null): boolean {
  // Vazio e null significam a mesma coisa aqui: não há recarga anterior conhecida.
  // `Number('')` é 0, que é finito — sem este teste, a marca vazia passaria pela guarda de
  // corrompida abaixo e seria lida como "recarregou no instante zero".
  if (marcaAnterior === null || marcaAnterior.trim() === '') return true;
  const anterior = Number(marcaAnterior);
  // Marca corrompida (outra aba, extensão, edição manual) não pode BLOQUEAR a recarga:
  // na dúvida recarregamos, porque o custo é um refresh e o de não recarregar é a tela presa.
  if (!Number.isFinite(anterior)) return true;
  return agora - anterior > JANELA_MS;
}

/**
 * Só o que este módulo realmente usa. `Pick<Window, 'addEventListener'>` traria as sobrecargas
 * inteiras do DOM e obrigaria qualquer dublê de teste a implementá-las — o `tsc` reprovou o
 * teste por isso. Declarar a superfície mínima é mais honesto e deixa o dublê trivial.
 */
export interface JanelaMinima {
  addEventListener(tipo: 'vite:preloadError', ouvinte: () => void): void;
  location: { reload(): void };
}

export function instalarRecargaDeChunk(
  win: JanelaMinima,
  storage: Pick<Storage, 'getItem' | 'setItem'>,
  agora: () => number = () => Date.now(),
  // Story 65.45: texto digitado e ainda não salvo só existe na memória da aba. Com ele, a
  // recarga não acontece: a tela mostra o aviso com "Tentar de novo" (RouteLoadBoundary).
  temTrabalhoGuardado: () => boolean = () => false,
): void {
  win.addEventListener('vite:preloadError', () => {
    if (temTrabalhoGuardado()) return;
    let marca: string | null = null;
    try {
      marca = storage.getItem(CHAVE_RECARGA);
    } catch {
      // sessionStorage bloqueado (modo restrito, terceiros): seguimos com marca nula, que
      // permite UMA recarga. Sem storage não há como travar o laço, mas travar o app é pior.
    }
    if (!deveRecarregar(agora(), marca)) return;
    try {
      storage.setItem(CHAVE_RECARGA, String(agora()));
    } catch {
      /* idem */
    }
    win.location.reload();
  });
}
